<template>
  <div>
    <Drawer
      v-model:visible="visible"
      :header="isThisEditMode ? 'Edit User' : 'View User'"
      position="right"
      :style="{ width: '600px' }"
      @hide="resetForm"
    >
      <div
        class="flex flex-col gap-8"
      >
        <div class="flex flex-col gap-4">
          <BaseInputLabel
            label="Profile"
          >
            <div class="w-full">
              <div class="flex justify-center items-center w-full">
                <img
                  v-if="previewUrl"
                  :src="previewUrl"
                  alt="Image"
                  class="rounded-full w-32 h-32 object-contain bg-black"
                >
                <div
                  v-else
                  class="flex flex-col items-center gap-2 w-full p-4 rounded-lg border"
                  :class="isThisEditMode ? '' : 'border-slate-300 bg-slate-200 text-slate-500'"
                >
                  <Icon

                    name="streamline:image-picture-landscape-2-photos-photo-landscape-picture-photography-camera-pictures"
                  />
                  <p class="text-xs">
                    No profile image
                  </p>
                </div>
              </div>
              <div
                v-if="isThisEditMode"
                class="mt-2"
              >
                <FileUpload
                  mode="basic"
                  customUpload
                  auto
                  severity="secondary"
                  class="p-button-outlined w-full"
                  accept="image/*"
                  @select="uploadProfileImage"
                />
              </div>
            </div>
          </BaseInputLabel>

          <BaseInputLabel
            label="Email"
            :isRequired="true"
          >
            <InputText
              v-model="activeForm.email"
              disabled
              fluid
            />
          </BaseInputLabel>

          <BaseInputLabel
            label="Name"
            :isRequired="true"
            :isErrored="errors.name"
            errorMessage="Name is required"
          >
            <InputText
              v-model="activeForm.name"
              :disabled="!isThisEditMode"
              :invalid="errors.name && submitted"
              fluid
            />
          </BaseInputLabel>

          <BaseInputLabel
            label="Phone"
            :isRequired="true"
            :isErrored="errors.phoneNumber"
            errorMessage="Valid phone is required"
          >
            <BasePhoneInputWithValidation
              ref="phoneInputRef"
              v-model:inputValue="activeForm.phoneNumber"
              v-model:isErrored="errors.phoneNumber"
              :isDisabled="isThisEditMode ? false : true"
            />
          </BaseInputLabel>

          <BaseInputLabel
            label="Role"
            :isRequired="true"
            :isErrored="errors.roleId"
            errorMessage="Role is required"
            :class="{
              'hidden ': hideStatus,
            }"
          >
            <Select
              v-model="activeForm.roleId"
              fluid
              :disabled="!isThisEditMode"
              option-label="name"
              option-value="id"
              :options="roles"
            />
          </BaseInputLabel>

          <BaseInputLabel
            label="Bio"
          >
            <div>
              <div
                v-if="!isThisEditMode"
                class="p-select p-component p-inputwrapper p-inputwrapper-filled p-disabled w-full"
              >
                <WidgetsRichTextEditor
                  v-model:data="activeForm.bio"
                  :isEditing="false"
                  class="p-select-label text-sm"
                />
              </div>

              <div v-else>
                <div class="border-t border-x border-gray-300 rounded-t-md p-1 flex gap-x-1 w-full">
                  <BoldControls v-model:editor="editor" />
                  <UnderlineControls v-model:editor="editor" />
                  <ItalicControls v-model:editor="editor" />
                </div>

                <EditorContent
                  class="border border-gray-300 rounded-b-md shadow-sm p-2"
                  :editor="editor"
                />
              </div>
            </div>
          </BaseInputLabel>

          <BaseInputLabel
            label="Youtube Link"
          >
            <InputText
              v-model="activeForm.youtubeLink"
              :disabled="!isThisEditMode"
              fluid
            />
          </BaseInputLabel>

          <BaseInputLabel
            label="Status"
            :isRequired="true"
            :class="{
              'hidden ': hideStatus,
            }"
          >
            <Select
              v-model="activeForm.isActive"
              :disabled="!isThisEditMode"
              option-label="label"
              option-value="value"
              :options="activeOptions"
              fluid
            />
          </BaseInputLabel>
        </div>

        <div
          v-if="isThisEditMode"
          class="flex flex-col gap-4"
        >
          <Button
            label="Save User"
            :loading="loading"
            @click="saveUser"
          />

          <Button
            outlined
            label="Cancel"
            :disabled="loading"
            @click="hideSidebar"
          />
        </div>
      </div>
    </Drawer>
  </div>
</template>

<script lang="ts" setup>
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import TextStyle from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import BoldControls from '~/components/Widgets/RichTextEditor/Bold.vue'
import UnderlineControls from '~/components/Widgets/RichTextEditor/Underline.vue'
import ItalicControls from '~/components/Widgets/RichTextEditor/Italic.vue'

const visible = defineModel('showUserSidebarVisible', {
  type: Boolean,
})
const loading = ref<boolean>(false)
const submitted = ref<boolean>(false)

const isThisEditMode = defineModel('isThisEditMode')
const activeUser = defineModel('activeUser')
const roles = defineModel('roles')
const hideStatus = defineModel('hideStatus', {
  default: false,
})

const emit = defineEmits(['load-users'])
const toast = useToast()

const activeForm = ref({
  email: '',
  name: '',
  phoneNumber: '',
  id: '',
  roleId: '',
  activeForm: '',
  url: '',
  awards: [],
  profileImgUrl: '',
  youtubeLink: '',
  isActive: true,
})

const errors = ref({
  name: false,
  phoneNumber: false,
})

const resetForm = () => {
  activeForm.value = {
    email: '',
    name: '',
    phoneNumber: '',
    id: '',
    roleId: '',
    activeForm: '',
    url: '',
    awards: [],
    profileImgUrl: '',
    youtubeLink: '',
    isActive: true,
  }

  errors.value = {
    name: false,
    phoneNumber: false,
  }
}

const hideSidebar = () => {
  visible.value = false
  submitted.value = false
  resetForm()
}

// Phone Input Component
const phoneInputRef = ref()

// Validate Phone
const formattedContactPhone = ref<string | undefined>()

const validatePhone = (phone: string) => {
  phoneInputRef.value.formatAndValidate(phone.toString())

  if (phoneInputRef.value.isValid) {
    formattedContactPhone.value = phoneInputRef.value.formattedInputNumber.number
  }

  return phoneInputRef.value.isValid
}

const validateForm = async (): Promise<boolean> => {
  errors.value.name = !activeForm.value.name.trim()
  errors.value.phoneNumber = !activeForm.value.phoneNumber?.trim() || !validatePhone(activeForm.value.phoneNumber)

  return !Object.values(errors.value).some(Boolean)
}

const activeOptions = ref([
  { label: 'Active', value: true },
  { label: 'Inactive', value: false },
])

const editor = ref('')

// file
const previewUrl = ref('')
const selectedFile = ref(null)
const uploadProfileImage = (event) => {
  const file = event.files[0]
  if (!file.type.startsWith('image/')) {
    toast.add({ severity: 'error', summary: 'Invalid File Type', detail: 'Please upload an image file.', life: 3000 })
    return
  }
  selectedFile.value = file
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
  }
  previewUrl.value = URL.createObjectURL(file)
}

const setUser = (activeUser) => {
  activeForm.value = {
    id: activeUser.userWorkSpaceId,
    phoneNumber: activeUser.phoneNumber,
    email: activeUser.email,
    name: activeUser.name,
    roleId: activeUser.roleId,
    bio: activeUser.bio,
    url: activeUser.url,
    awards: activeUser.awards,
    isActive: activeUser.isActive,
    youtubeLink: activeUser.youtubeLink,
    profileImgUrl: activeUser.profileImgUrl,
  }
  if (activeUser.bio) {
    editor.value.commands.setContent(activeUser.bio)
  }
  else {
    editor.value.commands.setContent('<p></p><p></p><p></p><p></p>')
  }

  previewUrl.value = activeUser.profileImgUrl || ''
}

watch(activeUser, async (newData, oldData) => {
  if (newData) {
    setUser(newData)
  }
})

watch(visible, async (newData, oldData) => {
  if (visible.value) {
    setUser(activeUser.value)
  }
})

const { uploadToPublicStorage } = useStorage()
const { updateUser } = useUsers()

const saveUser = async () => {
  submitted.value = true
  loading.value = true

  if (selectedFile.value) {
    // if there's a file selected then upload it
    try {
      const res = await uploadToPublicStorage({
        file: selectedFile.value,
      })
      activeForm.value.profileImgUrl = res.data.url
    }
    catch (error) {
      console.error('Upload failed:', error)
    }
  }

  try {
    const isValid = await validateForm()

    if (!isValid) {
      loading.value = false
      throw Error('Failed to update user - Invalid form data')
    }

    const res = await updateUser({
      ...activeForm.value,
      bio: editor.value.getHTML(),
    })

    if (!res) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update user',
        life: 3000,
      })
      return
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'User updated successfully',
      life: 3000,
    })

    hideSidebar()
    emit('load-users')
  }
  catch (error) {
    console.error('Error updating user:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update user',
      life: 3000,
    })
  }
  finally {
    loading.value = false
  }
}

onMounted(() => {
  editor.value = new Editor({
    content: '',
    extensions: [StarterKit, TextStyle, Underline],
  })
})
</script>
